// Wave 20.4 (web) — Activity detail page.
//
// Route: /activities/:id. For an outdoor cycling/running activity:
//   - Hero: type, date, duration, distance, elevation
//   - Peak intervals list (from GET /activities/:id/intervals)
//   - Elevation profile SVG (from the gps_track — reuses same
//     downsampling as the /shared/:token public page)
//   - Drop Link create — pick a window, get a URL to share
//
// Reuses SessionDetail's endpoints where they overlap; intervals +
// share come from the activities controller.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import IntensityCard from './components/IntensityCard';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import IosShareIcon from '@mui/icons-material/IosShare';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ShareCardDialog from './components/ShareCardDialog';
import UserPicker, { UserOption } from './components/UserPicker';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import PeaksPanel from './components/PeaksPanel';
import RouteMap, { type WindFrameSpec } from './components/RouteMap';
import SessionReplayControls, {
  useReplayClock,
  computeAutoSpeed,
  type ReplaySpeed,
} from './components/SessionReplayControls';
import DropLinkDialog, {
  type DropSubmit,
  type ShareWindow as _ShareWindow,
  type Visibility as _Visibility,
} from './components/DropLinkDialog';
import EditableSessionCopy from './components/EditableSessionCopy';
import ShareReplayDialog from './components/ShareReplayDialog';
import { WindStreakGlyph } from './components/cheerGlyphs';
import {
  fmtDistance as fmtDistanceUnits,
  fmtElevation as fmtElevationUnits,
  fmtPace as fmtPaceUnits,
  fmtRelativeWind,
  fmtSpeed as fmtSpeedUnits,
  fmtTemp,
  useUnitSystem,
} from './services/useUnitSystem';
import { colors } from './theme';
import { isRaphaDomain } from './services/subdomain';
import { useFeatures } from './services/features';

// Wave 20.38a — compass abbreviation for wind FROM-bearing.
const _COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
function bearingCompass(deg: number | null | undefined): string {
  if (deg == null) return '';
  const idx = Math.round(((deg % 360) + 360) % 360 / 22.5) % 16;
  return _COMPASS[idx];
}

const IS_RAPHA = isRaphaDomain();

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

type Visibility = 'public' | 'followers' | 'private';

type ActivityDetail = {
  id: string;
  type: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
  startedAt: string;
  endedAt: string | null;
  title: string | null;
  description: string | null;
  distanceM: number | null;
  elevationGainM: number | null;
  // Wave 20.41 — server-side aggregates for the summary cards.
  sensorHighlights?: {
    avg_hr_bpm?: number | null;
    max_hr_bpm?: number | null;
    avg_power_w?: number | null;
    max_power_w?: number | null;
  } | null;
  elevationLossM: number | null;
  avgSpeedMps: number | null;
  maxSpeedMps: number | null;
  visibility?: Visibility;
  // `null` entries are privacy-zone segment breaks; renderers
  // that draw the polyline in segments (feed thumbnail via
  // Mapbox static path overlays) look for them. Renderers that
  // want a scalar-over-time view (elevation profile) filter
  // nulls out first.
  gpsTrack: Array<{ t: number; lat: number; lng: number; alt: number; speed: number } | null> | null;
  // Wave 20.38c — server-side sensor time series (power / HR /
  // cadence). Nullable: outdoor sessions without a paired power
  // meter or HR strap won't have it.
  timeSeries?: Array<{
    t: number;
    power_w?: number | null;
    hr_bpm?: number | null;
    cadence_rpm?: number | null;
  }> | null;
  // Wave 20.28 — thrucast rollup from the weather service.
  // Null for indoor sessions or before ingest catches up.
  weatherSummary: {
    condition: 'clear' | 'cloudy' | 'rain' | 'snow' | 'mixed' | 'unknown';
    temperature_c: { avg: number | null; min: number | null; max: number | null };
    humidity_pct: number | null;
    precip_mm_total: number | null;
    wind_speed_ms: { avg: number | null; max: number | null };
    wind_from_deg: number | null;
    source_run: string | null;
  } | null;
};

type Interval = {
  kind: string;
  value: number;
  unit: string;
  startSec: number;
  endSec: number;
};

const SHARE_WINDOWS: Array<{ value: string; label: string }> = [
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: 'forever', label: 'Never expires' },
];

function tokenHeader(): Record<string, string> {
  const t = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

function formatDistance(m: number | null): string {
  if (m == null || m === 0) return '—';
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m} m`;
}

function formatSpeed(mps: number | null): string {
  if (mps == null || mps === 0) return '—';
  return `${(mps * 3.6).toFixed(1)} km/h`;
}

// Wave 20.26 — Pace = time per distance (min/km). Only makes sense
// for runs; cycling stays on speed. Handles walking / near-zero
// input by returning '—' instead of infinity.
function formatPace(mps: number | null): string {
  if (mps == null || mps <= 0.1) return '—';
  const secPerKm = 1000 / mps;
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
}

function formatIntervalValue(iv: Interval): string {
  switch (iv.unit) {
    case 'W':
      return `${Math.round(iv.value)} W`;
    case 'mps':
      return formatSpeed(iv.value);
    case 'sec_per_m': {
      const secPerKm = iv.value * 1000;
      const m = Math.floor(secPerKm / 60);
      const s = Math.round(secPerKm % 60);
      return `${m}:${String(s).padStart(2, '0')} /km`;
    }
    case 'sec':
      return formatDuration(iv.value);
    case 'm':
      return formatDistance(iv.value);
    case 'm_per_h':
      return `${Math.round(iv.value)} m/h`;
    default:
      return `${iv.value}`;
  }
}

// Wave 20.37 — pretty-print target tokens ("5k" → "5K", "half" →
// "Half Marathon") so kind names like peak_pace_5k render cleanly.
function prettyTarget(t: string): string {
  const map: Record<string, string> = {
    '5s': '5s', '30s': '30s',
    '1min': '1min', '5min': '5min', '20min': '20min', '60min': '60min',
    '1mi': '1mi', '5k': '5K', '10k': '10K', '40k': '40K',
    'half': 'Half Marathon', 'full': 'Marathon',
  };
  return map[t] ?? t;
}

function kindLabel(kind: string): string {
  // Wave 20.46 — whole-run + per-metric sub-effort rows.
  if (kind === 'peak_whole') return 'Entire session';
  if (kind === 'peak_pace') return 'Peak Pace';
  if (kind === 'peak_speed') return 'Peak Speed';
  if (kind === 'peak_power') return 'Peak Power';
  // Wave 20.49ag — cycling shows Grade-Adj SPEED so the label
  // matches the mph/kph value format. Note: this function is
  // currently defined but not called from the ActivityDetail
  // render tree (PeaksPanel has its own copy) — updating for
  // future callers.
  if (kind === 'peak_gap') return 'Peak Grade-Adj Speed';
  if (kind === 'peak_vam') return 'Peak Climb (VAM)';
  // Legacy kinds — kept so old rows still label sensibly until the
  // on-view backfill re-detects them under the current spec.
  if (kind === 'peak_grade_adj_speed') return 'Peak Speed';
  if (kind === 'peak_grade_adj_pace') return 'Peak Pace';
  const parts = kind.split('_');
  if (parts[0] === 'peak' && parts[1] === 'power' && parts[2]) return `Best ${prettyTarget(parts[2])} Power`;
  if (parts[0] === 'peak' && parts[1] === 'speed' && parts[2]) return `Fastest ${prettyTarget(parts[2])}`;
  if (parts[0] === 'peak' && parts[1] === 'pace' && parts[2]) return `Fastest ${prettyTarget(parts[2])}`;
  if (kind === 'effort_zone4_max_sec') return 'Longest Zone 4+ Effort';
  return kind;
}

function activityLabel(type: string): string {
  if (type === 'outdoor_cycling') return 'Ride';
  if (type === 'outdoor_running') return 'Run';
  return 'Session';
}

// Wave 20.38c — unified session replay HUD. Renders every live
// metric the rider expects to see move as the playback advances:
// time, distance, speed / pace, altitude, power, heart rate, then
// weather (temp, humidity, wind speed + direction, condition).
// Anchor + size are parent-controlled so this can be an inline
// strip under the small map or a big top-strip in fullscreen.
type ReplayHudProps = {
  anchor: 'top' | 'inline';
  large?: boolean;
  replayPos: number;
  isRunning: boolean;
  sample: {
    speed: number;
    alt: number;
    distanceM: number;
  } | null;
  sensor: {
    power_w?: number | null;
    hr_bpm?: number | null;
    cadence_rpm?: number | null;
  } | null;
  wind: WindFrameSpec | null;
  units: ReturnType<typeof useUnitSystem>;
};
function formatReplayClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}
function compassLabel(deg: number | null | undefined): string {
  if (deg == null) return '';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
}
function ReplayHudStrip({ anchor, large, replayPos, isRunning, sample, sensor, wind, units }: ReplayHudProps) {
  // Wave 20.49cf — auto-compact on narrow viewports (mobile-web
  // replay); otherwise the top HUD overflows and the terrain chip
  // stacks over it.
  const isNarrow = useMediaQuery('(max-width:600px)');
  const effectiveLarge = large && !isNarrow;
  const labelSize = effectiveLarge ? '0.6rem' : isNarrow ? '0.5rem' : '0.55rem';
  const valSize = effectiveLarge ? '1.05rem' : isNarrow ? '0.8rem' : '0.85rem';
  const cellGap = effectiveLarge ? 2.5 : isNarrow ? 1.25 : 1.5;
  const cellMinWidth = isNarrow ? 56 : 64;
  const containerSx = anchor === 'top'
    ? {
        position: 'absolute' as const,
        top: 12,
        left: 12,
        right: 12,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        backgroundColor: 'rgba(15,15,20,0.88)',
        border: `1px solid ${colors.border}`,
        backdropFilter: 'blur(6px)',
        pointerEvents: 'none' as const,
      }
    : {
        mt: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      };
  const Cell = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ minWidth: cellMinWidth }}>
      <Typography
        sx={{
          color: colors.textMuted,
          fontSize: labelSize,
          fontWeight: 800,
          letterSpacing: '0.15em',
          lineHeight: 1,
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: colors.text,
          fontSize: valSize,
          fontWeight: 700,
          fontFamily: '"JetBrains Mono", monospace',
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
  const distanceStr = sample ? fmtDistanceUnits(sample.distanceM, units) : '—';
  const speedStr = sample
    ? isRunning
      ? fmtPaceUnits(sample.speed, units)
      : fmtSpeedUnits(sample.speed, units)
    : '—';
  const altStr = sample ? fmtElevationUnits(sample.alt, units) : '—';
  const powerStr = sensor?.power_w != null ? `${Math.round(sensor.power_w)} W` : null;
  const hrStr = sensor?.hr_bpm != null ? `${Math.round(sensor.hr_bpm)} bpm` : null;
  const tempStr = wind?.temperature_c != null ? fmtTemp(wind.temperature_c, units) : null;
  const humStr = wind?.humidity_pct != null ? `${Math.round(wind.humidity_pct)}%` : null;
  const windStr = wind?.wind_speed_ms != null
    ? `${fmtSpeedUnits(wind.wind_speed_ms, units)}${wind.wind_from_deg != null ? ` ${compassLabel(wind.wind_from_deg)}` : ''}`
    : null;
  return (
    <Box sx={containerSx}>
      <Stack direction="row" spacing={cellGap} alignItems="center" flexWrap="wrap">
        <Cell label="TIME" value={formatReplayClock(replayPos)} />
        <Cell label="DIST" value={distanceStr} />
        <Cell label={isRunning ? 'PACE' : 'SPEED'} value={speedStr} />
        <Cell label="ALT" value={altStr} />
        {powerStr && <Cell label="POWER" value={powerStr} />}
        {hrStr && <Cell label="HR" value={hrStr} />}
        {tempStr && <Cell label="TEMP" value={tempStr} />}
        {humStr && <Cell label="HUM" value={humStr} />}
        {windStr && <Cell label="WIND" value={windStr} />}
        {wind?.condition && (
          <Cell label="COND" value={wind.condition.toUpperCase()} />
        )}
      </Stack>
    </Box>
  );
}

export default function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const features = useFeatures();
  const navigate = useNavigate();
  const units = useUnitSystem();
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [shareReplayOpen, setShareReplayOpen] = useState(false);
  // Wave 20.47 — when we arrive from a fresh save (?saved=1), auto-
  // open the title/description editor pre-filled with the server
  // recommendation so the rider can accept or tweak before the
  // session hits the feed.
  const [autoOpenCopy, setAutoOpenCopy] = useState(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('saved') === '1',
  );
  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  // Wave 20.49cb — Session media (photos + short videos). State
  // lives here so RouteMap can render 📷 markers at each pin AND
  // MediaSection can share the same items + viewer state.
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaViewerId, setMediaViewerId] = useState<string | null>(null);
  const loadMedia = useCallback(async () => {
    if (!activity?.id) return;
    try {
      const res = await fetch(`${API}/sessions/${activity.id}/media`, {
        headers: tokenHeader(),
      });
      if (!res.ok) return;
      setMediaItems(await res.json());
    } catch { /* silent */ }
  }, [activity?.id]);
  useEffect(() => {
    void loadMedia();
  }, [loadMedia]);

  // Wave 20.27 — Unified drop-link intent. Every drop trigger
  // (activity, peak, session-total stat) opens the same
  // DropLinkDialog with a payload; onSubmit dispatches to the right
  // API. Consolidates the three earlier one-off share paths.
  type DropIntent =
    | { kind: 'activity' }
    | {
        kind: 'peak';
        peakKind: string;
        value: number;
        unit: string;
        contextLabel: string;
        // Wave 20.31 — effort window (either or both may be null).
        durationSec?: number | null;
        distanceM?: number | null;
      };
  const [pendingDrop, setPendingDrop] = useState<DropIntent | null>(null);
  // Wave 20.49cp — Tier-1 social share: canvas card → OS share sheet.
  const [shareCardOpen, setShareCardOpen] = useState(false);

  // Wave 20.32 — one peak currently spotlighted on the map + elevation.
  // Clicking the same peak again clears the selection.
  const [selectedPeak, setSelectedPeak] = useState<{
    kind: string;
    startSec: number;
    endSec: number;
    valueLabel: string;
    durationLabel: string;
    distanceLabel: string | null;
  } | null>(null);

  const submitDrop = useCallback(
    async (opts: DropSubmit): Promise<{ url: string } | void> => {
      if (!id || !pendingDrop) return;
      if (pendingDrop.kind === 'activity') {
        const res = await fetch(`${API}/activities/${id}/share`, {
          method: 'POST',
          headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            window: opts.window,
            visibility: opts.visibility,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.message || `share ${res.status}`);
        }
        const data = await res.json();
        if (opts.recipients.length > 0) {
          await fetch(`${API}/drops/send`, {
            method: 'POST',
            headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dropType: 'activity',
              token: data.token,
              recipientIds: opts.recipients.map((u) => u.id),
            }),
          }).catch(() => {
            /* non-fatal — URL still works */
          });
        }
        return { url: data.url };
      }
      // peak / stat drop → /peaks/share
      const res = await fetch(`${API}/peaks/share`, {
        method: 'POST',
        headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: pendingDrop.peakKind,
          value: pendingDrop.value,
          unit: pendingDrop.unit,
          contextLabel: pendingDrop.contextLabel,
          durationSec: pendingDrop.durationSec ?? null,
          distanceM: pendingDrop.distanceM ?? null,
          // Preserve creator's unit preference so the public page
          // renders the exact string they saw when dropping.
          displayUnits: units,
          window: opts.window,
          visibility: opts.visibility === 'private' ? 'public' : opts.visibility,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `share ${res.status}`);
      }
      const data = await res.json();
      if (opts.recipients.length > 0) {
        await fetch(`${API}/drops/send`, {
          method: 'POST',
          headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dropType: 'peak',
            token: data.token,
            recipientIds: opts.recipients.map((u) => u.id),
          }),
        }).catch(() => {});
      }
      return { url: data.url };
    },
    [id, pendingDrop],
  );

  const dropStat = useCallback(
    (
      kind: string,
      value: number | null | undefined,
      unit: string,
      contextLabel: string,
    ) => {
      if (value == null || value <= 0) return;
      setPendingDrop({ kind: 'peak', peakKind: kind, value, unit, contextLabel });
    },
    [],
  );
  // Wave 20.37 — feature-gated dropStat handler generator. Returns
  // undefined on tiers without drops so StatTile's `onDrop &&` gate
  // hides the button entirely.
  const mkDropHandler = useCallback(
    (kind: string, value: number | null | undefined, unit: string, ctx: string) =>
      features.drops ? () => dropStat(kind, value, unit, ctx) : undefined,
    [features.drops, dropStat],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = tokenHeader();
        const [aRes, iRes] = await Promise.all([
          fetch(`${API}/sessions/${id}/detail?segments=1`, { headers }),
          fetch(`${API}/activities/${id}/intervals`, { headers }),
        ]);
        if (!aRes.ok) throw new Error('activity ' + aRes.status);
        const aData = await aRes.json();
        if (cancelled) return;
        setActivity(aData);
        if (iRes.ok) setIntervals(await iRes.json());
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Wave 20.32 — memoize points + highlights so their referential
  // identity is stable across re-renders. Without this, RouteMap's
  // main useEffect saw a new `points` array every parent re-render,
  // tore down the map, and raced with the "add selected-peak source"
  // effect ("Style is not done loading").
  const routePoints = useMemo(
    () =>
      activity?.gpsTrack
        ? activity.gpsTrack
            .filter((p): p is NonNullable<typeof p> => p != null)
            .map((p) => ({ lat: p.lat, lng: p.lng, t: p.t }))
        : [],
    [activity],
  );

  // Wave 20.38a — Session replay state. Duration is the elapsed
  // window between session start and end (matches the summary
  // card). Position advances via useReplayClock; onEnd auto-pauses.
  // Wave 20.49bq — nulls-filtered "compact" track for the replay
  // clock / interpolation. Privacy-zone segment breaks (?segments=1)
  // put null entries into gpsTrack; the replay treats those as
  // continuous for playback purposes.
  const compactTrack = useMemo(
    () =>
      (activity?.gpsTrack ?? []).filter(
        (p): p is NonNullable<typeof p> => p != null,
      ),
    [activity],
  );
  const replayDurationSec = useMemo(() => {
    if (compactTrack.length < 2) return 0;
    return Math.max(0, compactTrack[compactTrack.length - 1].t - compactTrack[0].t);
  }, [compactTrack]);
  // Wave 20.38c — Auto-speed = compress full playback into
  // REPLAY_TARGET_WALL_SEC seconds regardless of session length. A
  // 60-min ride shows the whole thing in ~15 s at 240x. User can
  // dial down from there.
  const replayAutoSpeed = useMemo(
    () => computeAutoSpeed(replayDurationSec),
    [replayDurationSec],
  );
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState<ReplaySpeed>(1);
  const [replayFullscreen, setReplayFullscreen] = useState(false);
  // Wave 20.38b — terrain exaggeration knob. 3x default reads well
  // even on rolling routes; slider lets riders push to 10x for very
  // flat rides where the DEM signal is otherwise imperceptible.
  const [terrainExag, setTerrainExag] = useState<number>(3);
  // Wave 20.38c — current wind frame (temp / humidity / wind speed
  // + dir) at the playback moment. RouteMap fetches the manifest
  // and drives the pin; it pushes the frame up via onWeatherChange
  // so the fullscreen HUD can show weather alongside sensor data
  // without duplicating the fetch.
  const [currentWind, setCurrentWind] = useState<WindFrameSpec | null>(null);
  const { positionSec: replayPos, setPositionSec: setReplayPos } = useReplayClock(
    replayDurationSec,
    replayPlaying,
    replaySpeed,
    () => setReplayPlaying(false),
  );
  // Wave 20.46d — replay is fullscreen-only + tap-to-start (parity
  // with mobile). Auto-play only fires when the user opens the
  // fullscreen Dialog; closing pauses + rewinds so re-open restarts
  // from 0. Previous auto-on-mount drove 60 fps re-renders on the
  // inline map, which — while fine on desktop performance-wise —
  // was inconsistent with mobile and cluttered the session-detail
  // page with a replay HUD nobody asked for.
  useEffect(() => {
    if (!replayFullscreen) return;
    if (replayDurationSec <= 0) return;
    setReplaySpeed(replayAutoSpeed);
    setReplayPos(0);
    setReplayPlaying(true);
  }, [replayFullscreen, replayDurationSec, replayAutoSpeed, setReplayPos]);
  useEffect(() => {
    if (replayFullscreen) return;
    setReplayPlaying(false);
    setReplayPos(0);
  }, [replayFullscreen, setReplayPos]);
  // Sample gpsTrack at the current playback time via linear interp
  // between the two nearest samples. Cheap enough at 60 fps for a
  // few-thousand-point track; upgrade to a memoised binary-search
  // cursor if it ever becomes hot.
  // Wave 20.38c — cumulative distance per GPS sample, computed
  // once per activity load so the replay HUD can show "distance so
  // far" without recomputing haversine per frame.
  const gpsCumDist = useMemo(() => {
    const track = compactTrack;
    if (!track || track.length === 0) return null;
    const out = new Float64Array(track.length);
    for (let i = 1; i < track.length; i++) {
      const a = track[i - 1];
      const b = track[i];
      const R = 6371000;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const s1 = Math.sin(dLat / 2);
      const s2 = Math.sin(dLng / 2);
      const q =
        s1 * s1 +
        Math.cos((a.lat * Math.PI) / 180) *
          Math.cos((b.lat * Math.PI) / 180) *
          s2 * s2;
      const d = 2 * R * Math.asin(Math.min(1, Math.sqrt(q)));
      out[i] = out[i - 1] + d;
    }
    return out;
  }, [activity]);
  const currentSample = useMemo(() => {
    const track = compactTrack;
    if (!track || track.length === 0) return null;
    const t0 = track[0].t;
    const target = t0 + replayPos;
    if (target <= track[0].t) {
      const s = track[0];
      return {
        t: target,
        lat: s.lat,
        lng: s.lng,
        alt: s.alt,
        speed: s.speed,
        distanceM: 0,
      };
    }
    if (target >= track[track.length - 1].t) {
      const s = track[track.length - 1];
      return {
        t: target,
        lat: s.lat,
        lng: s.lng,
        alt: s.alt,
        speed: s.speed,
        distanceM: gpsCumDist ? gpsCumDist[track.length - 1] : 0,
      };
    }
    let lo = 0;
    let hi = track.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (track[mid].t <= target) lo = mid;
      else hi = mid;
    }
    const a = track[lo];
    const b = track[hi];
    const alpha = (target - a.t) / Math.max(1e-6, b.t - a.t);
    const dA = gpsCumDist ? gpsCumDist[lo] : 0;
    const dB = gpsCumDist ? gpsCumDist[hi] : 0;
    return {
      t: target,
      lat: a.lat + (b.lat - a.lat) * alpha,
      lng: a.lng + (b.lng - a.lng) * alpha,
      alt: a.alt + (b.alt - a.alt) * alpha,
      speed: a.speed + (b.speed - a.speed) * alpha,
      distanceM: dA + (dB - dA) * alpha,
    };
  }, [activity, replayPos, gpsCumDist]);
  // Wave 20.38c — interp timeSeries (power / HR / cadence) at the
  // current replay position. Separate from gpsTrack because
  // sensors sample at different rates.
  const currentSensor = useMemo(() => {
    const ts = activity?.timeSeries;
    if (!ts || ts.length === 0) return null;
    const target = replayPos;
    if (target <= ts[0].t) return ts[0];
    if (target >= ts[ts.length - 1].t) return ts[ts.length - 1];
    let lo = 0;
    let hi = ts.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (ts[mid].t <= target) lo = mid;
      else hi = mid;
    }
    // Sensor readings that are null/undefined for the low sample
    // shouldn't be linearly interpolated — just carry the nearest
    // defined value.
    const a = ts[lo];
    const b = ts[hi];
    const alpha = (target - a.t) / Math.max(1e-6, b.t - a.t);
    const lerp = (av?: number | null, bv?: number | null) => {
      if (av == null && bv == null) return null;
      if (av == null) return bv ?? null;
      if (bv == null) return av;
      return av + (bv - av) * alpha;
    };
    return {
      t: target,
      power_w: lerp(a.power_w, b.power_w),
      hr_bpm: lerp(a.hr_bpm, b.hr_bpm),
      cadence_rpm: lerp(a.cadence_rpm, b.cadence_rpm),
    };
  }, [activity, replayPos]);
  const playbackMarker = useMemo(
    () => (currentSample ? { lat: currentSample.lat, lng: currentSample.lng } : null),
    [currentSample],
  );
  const routeHighlights = useMemo(
    () =>
      intervals.map((iv) => ({
        startSec: iv.startSec,
        endSec: iv.endSec,
        label: iv.kind,
      })),
    [intervals],
  );

  const elevationProfile = useMemo(() => {
    if (!activity?.gpsTrack || activity.gpsTrack.length < 3) return null;
    // Downsample to 200 points max for the SVG.
    // Filter out privacy-zone null sentinels — elevation profile
    // is a scalar-over-time plot; a gap in the polyline still
    // gets a smooth elevation reading between the two edges.
    const pts = activity.gpsTrack.filter(
      (p): p is NonNullable<typeof p> => p != null,
    );
    if (pts.length < 3) return null;
    const stride = Math.max(1, Math.floor(pts.length / 200));
    const sampled: Array<[number, number]> = [];
    for (let i = 0; i < pts.length; i += stride) sampled.push([pts[i].t, pts[i].alt]);
    return sampled;
  }, [activity]);

  if (error) {
    return (
      <PageLayout maxWidth="md">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Couldn't load session</Typography>
          <Typography sx={{ color: colors.textMuted }}>{error}</Typography>
        </Box>
      </PageLayout>
    );
  }

  if (!activity) {
    return (
      <PageLayout maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }

  const durationSec = activity.endedAt
    ? Math.round((new Date(activity.endedAt).getTime() - new Date(activity.startedAt).getTime()) / 1000)
    : 0;

  return (
    <PageLayout maxWidth="md">
      {/* Wave 20.49cg — belt-and-suspenders: prevent any child
          section (Peaks rows, elevation labels, replay dialog
          overlays) from pushing the whole ActivityDetail page
          into horizontal overflow on mobile-web. */}
      <Stack spacing={3} sx={{ overflowX: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/feed')} sx={{ color: colors.text }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem' }}>
            Back to feed
          </Typography>
        </Stack>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: colors.brand }}>
              {activityLabel(activity.type)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Wave 20.49cq — Share card (Instagram / Facebook via
                  the OS share sheet on mobile browsers; download +
                  caption on desktop). Labeled, not a bare icon, and
                  in the same header row as mobile's control so the
                  affordance matches on both platforms. */}
              <Button
                size="small"
                onClick={() => setShareCardOpen(true)}
                startIcon={<PhotoCameraIcon fontSize="small" />}
                title="Share a card to Instagram or Facebook"
                sx={{
                  color: colors.brand,
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  letterSpacing: '0.04em',
                  textTransform: 'none',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  border: `1px solid ${colors.border}`,
                  '&:hover': { borderColor: colors.brand, backgroundColor: `${colors.brand}14` },
                }}
              >
                Share card
              </Button>
              <VisibilityChip
                value={(activity.visibility ?? 'public') as Visibility}
                onChange={async (next) => {
                  if (!id) return;
                  try {
                    const res = await fetch(`${API}/activities/${id}/visibility`, {
                      method: 'PATCH',
                      headers: { ...tokenHeader(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({ visibility: next }),
                    });
                    if (!res.ok) throw new Error('update ' + res.status);
                    setActivity({ ...activity, visibility: next });
                    setSnack(
                      next === 'public'
                        ? 'Session set to Anyone'
                        : next === 'followers'
                        ? 'Session set to Friends'
                        : 'Session set to Just me — sharing disabled',
                    );
                  } catch (e: any) {
                    setSnack(e?.message || 'Update failed');
                  }
                }}
              />
              {features.drops && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPendingDrop({ kind: 'activity' })}
                  sx={{
                    color: colors.brand,
                    borderColor: colors.brand,
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  Drop link
                </Button>
              )}
              {/* Wave 20.26 — delete this activity. Confirms first via
                  window.confirm to keep the surface minimal (a proper
                  Dialog is overkill for a self-owned row). */}
              <Button
                size="small"
                variant="text"
                onClick={async () => {
                  if (!id) return;
                  if (!window.confirm('Delete this session? This is permanent.')) return;
                  try {
                    const res = await fetch(`${API}/activities/${id}`, {
                      method: 'DELETE',
                      headers: tokenHeader(),
                    });
                    if (!res.ok && res.status !== 204) throw new Error('delete ' + res.status);
                    navigate('/feed');
                  } catch (e: any) {
                    setSnack(e?.message || 'Delete failed');
                  }
                }}
                sx={{ color: colors.danger, fontWeight: 700, textTransform: 'none' }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {new Date(activity.startedAt).toLocaleDateString()} · {formatDuration(durationSec)}
          </Typography>
          {/* Wave 20.38a — WeatherChip removed here; the same info now
              renders as StatTiles alongside the primary metrics below
              (no share icons, since weather isn't drop-linkable). */}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          <StatTile
            label="Distance"
            value={fmtDistanceUnits(activity.distanceM, units)}
            onDrop={mkDropHandler(
              'session_distance',
              activity.distanceM,
              'm',
              `${activityLabel(activity.type)} · ${fmtDistanceUnits(activity.distanceM, units)}`,
            )}
          />
          <StatTile
            label="Time"
            value={formatDuration(durationSec)}
            onDrop={mkDropHandler(
              'session_duration',
              durationSec,
              'sec',
              `${activityLabel(activity.type)} · ${formatDuration(durationSec)}`,
            )}
          />
          <StatTile
            label="Elev gain"
            value={fmtElevationUnits(activity.elevationGainM, units)}
            onDrop={mkDropHandler(
              'session_elevation_gain',
              activity.elevationGainM,
              'm',
              `${activityLabel(activity.type)} · ${fmtElevationUnits(activity.elevationGainM, units)} elev`,
            )}
          />
          {activity.type === 'outdoor_running' ? (
            <>
              <StatTile
                label="Avg pace"
                value={fmtPaceUnits(activity.avgSpeedMps, units)}
                onDrop={mkDropHandler(
                  'session_avg_pace',
                  activity.avgSpeedMps,
                  'mps',
                  `Run · avg ${fmtPaceUnits(activity.avgSpeedMps, units)}`,
                )}
              />
              {/* Wave 20.49ah — Best peak / max speed tile
                  removed from the top row. Covered by the
                  peaks section below; the top row was noisy
                  with max + avg both showing. Intensity card
                  (rendered below) replaces the signal. */}
            </>
          ) : (
            <>
              <StatTile
                label="Avg speed"
                value={fmtSpeedUnits(activity.avgSpeedMps, units)}
                onDrop={mkDropHandler(
                  'session_avg_speed',
                  activity.avgSpeedMps,
                  'mps',
                  `Ride · avg ${fmtSpeedUnits(activity.avgSpeedMps, units)}`,
                )}
              />
              {/* Wave 20.49ah — Best peak / max speed tile
                  removed. See above. */}
            </>
          )}
          {/* Wave 20.41 — sensor summary cards. Only rendered when
              the rider actually paired the sensor for this session.
              Values come from server-side sensorHighlights (already
              computed at ingest); rendering is display only. */}
          {activity.sensorHighlights?.avg_power_w != null && (
            <StatTile
              label="Avg power"
              value={`${Math.round(activity.sensorHighlights.avg_power_w)} W`}
              onDrop={mkDropHandler(
                'session_avg_power',
                activity.sensorHighlights.avg_power_w,
                'W',
                `${activityLabel(activity.type)} · avg ${Math.round(
                  activity.sensorHighlights.avg_power_w,
                )} W`,
              )}
            />
          )}
          {activity.sensorHighlights?.avg_hr_bpm != null && (
            <StatTile
              label="Avg HR"
              value={`${Math.round(activity.sensorHighlights.avg_hr_bpm)} bpm`}
              onDrop={mkDropHandler(
                'session_avg_hr',
                activity.sensorHighlights.avg_hr_bpm,
                'bpm',
                `${activityLabel(activity.type)} · avg ${Math.round(
                  activity.sensorHighlights.avg_hr_bpm,
                )} bpm`,
              )}
            />
          )}
        </Box>

        {/* Wave 20.49ah — Intensity vs the rider's own history.
            Renders "N more sessions to unlock" until they have
            3 prior same-type sessions, then a percentile chip
            + min→max bar with median tick + this session's
            marker. */}
        {id && <IntensityCard sessionId={id} />}

        {/* Wave 20.38a — Weather averages for the session. Rendered
            as read-only StatTiles (no onDrop → no share icon) since
            weather isn't a metric a rider wants to individually drop-
            link. Only appears when the weather rollup is present. */}
        {activity.weatherSummary && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: 1.5,
            }}
          >
            {activity.weatherSummary.temperature_c.avg != null && (
              <StatTile
                label="Avg temp"
                value={fmtTemp(activity.weatherSummary.temperature_c.avg, units)}
              />
            )}
            {activity.weatherSummary.wind_speed_ms.avg != null &&
              activity.weatherSummary.wind_speed_ms.avg > 0 && (
              <StatTile
                label="Avg wind"
                value={`${fmtSpeedUnits(activity.weatherSummary.wind_speed_ms.avg, units)}${
                  activity.weatherSummary.wind_from_deg != null
                    ? ` ${bearingCompass(activity.weatherSummary.wind_from_deg)}`
                    : ''
                }`}
              />
            )}
            {activity.weatherSummary.humidity_pct != null && (
              <StatTile
                label="Avg humidity"
                value={`${Math.round(activity.weatherSummary.humidity_pct)}%`}
              />
            )}
            {/* Wave 20.42 — Avg relative headwind. Signed value:
                positive = net headwind, negative = tailwind. Read-only
                (no onDrop) per user direction — the metric isn't drop-
                shareable, it's context for reading the session. */}
            {(activity.weatherSummary as any).avg_headwind_mps != null && (
              <StatTile
                label="Wind Conditions"
                icon={<WindStreakGlyph size={18} />}
                value={fmtRelativeWind(
                  (activity.weatherSummary as any).avg_headwind_mps,
                  units,
                ).label}
              />
            )}
          </Box>
        )}

        {/* Wave 20.47 — rider-authored title + description, editable
            in-place. Auto-opens the editor when we land from a fresh
            save (?saved=1) so the rider accepts/tweaks the server
            recommendation before it hits the feed. */}
        <EditableSessionCopy
          sessionId={activity.id}
          title={activity.title}
          description={activity.description}
          autoOpenIfEmpty={autoOpenCopy}
          onSaved={(next) => {
            setActivity((cur) => (cur ? { ...cur, title: next.title, description: next.description } : cur));
            setAutoOpenCopy(false);
          }}
        />

        {/* Wave 20.26 — Route map. Only for outdoor sessions with an
            actual GPS trace. Non-interactive; the detail context is
            "look at where you rode," not "explore the map." */}
        {activity.gpsTrack && activity.gpsTrack.length >= 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Map</Typography>
            {/* Wave 20.46d — inline map is a static preview + peaks
                explorer. No marker animation, no camera follow. The
                animated replay lives in the fullscreen Dialog below. */}
            <RouteMap
              points={routePoints}
              highlights={routeHighlights}
              selectedPeak={selectedPeak ? {
                startSec: selectedPeak.startSec,
                endSec: selectedPeak.endSec,
                valueLabel: selectedPeak.valueLabel,
                durationLabel: selectedPeak.durationLabel,
                distanceLabel: selectedPeak.distanceLabel,
              } : null}
              windReplay={activity.endedAt ? {
                startedAt: activity.startedAt,
                endedAt: activity.endedAt,
              } : undefined}
              terrain3D={features.terrain3D}
              terrainExaggeration={terrainExag}
              onWeatherChange={setCurrentWind}
              mediaPins={mediaItems
                .filter((m) => m.lat != null && m.lng != null)
                .map((m) => ({ id: m.id, lat: m.lat as number, lng: m.lng as number }))}
              onMediaPinClick={setMediaViewerId}
            />
            {intervals.length > 0 && (
              <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: 1 }}>
                Bright segments = detected peaks.
              </Typography>
            )}
            {/* Wave 20.38b — terrain exaggeration slider. Only shown
                when 3D terrain is enabled (features flag). */}
            {features.terrain3D && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mt: 1 }}
              >
                <Typography
                  sx={{
                    color: colors.textMuted,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    minWidth: 68,
                  }}
                >
                  TERRAIN ×{terrainExag.toFixed(0)}
                </Typography>
                <Slider
                  size="small"
                  min={1}
                  max={10}
                  step={1}
                  value={terrainExag}
                  onChange={(_, v) =>
                    setTerrainExag(Array.isArray(v) ? v[0] : v)
                  }
                  marks={[
                    { value: 1, label: '1x' },
                    { value: 5, label: '5x' },
                    { value: 10, label: '10x' },
                  ]}
                  sx={{
                    color: colors.brand,
                    maxWidth: 320,
                    '& .MuiSlider-markLabel': {
                      color: colors.textMuted,
                      fontSize: '0.65rem',
                    },
                  }}
                />
              </Stack>
            )}
            {/* Wave 20.46d — one big "Replay ride" button opens the
                fullscreen Dialog. Replaces the inline SessionReplay
                controls + HUD that used to auto-play beneath the map. */}
            {replayDurationSec > 0 && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setReplayFullscreen(true)}
                  sx={{
                    py: 1.4,
                    fontWeight: 800,
                    letterSpacing: '0.03em',
                    backgroundColor: colors.brand,
                    color: '#0a100c',
                    '&:hover': { backgroundColor: colors.brand, opacity: 0.9 },
                  }}
                >
                  ▶  Replay ride
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShareReplayOpen(true)}
                  sx={{
                    py: 1.4,
                    fontWeight: 800,
                    letterSpacing: '0.03em',
                    color: colors.brand,
                    borderColor: colors.brand,
                    minWidth: 180,
                  }}
                >
                  Drop replay
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {/* Wave 20.49ca — Web parity for session media. Same
            add-a-photo → thumbnail grid → fullscreen viewer flow
            as the mobile SessionDetailScreen. Owner-only backend;
            /media/:id needs a Bearer token so we fetch via
            AuthImage (blob URL) instead of raw <img src>. */}
        <MediaSection
          sessionId={activity.id}
          activityGpsFirst={
            activity.gpsTrack
              ? (() => {
                  const p = activity.gpsTrack.find((x) => x != null);
                  return p ? { lat: p.lat, lng: p.lng } : null;
                })()
              : null
          }
          items={mediaItems}
          onReload={loadMedia}
          viewerId={mediaViewerId}
          setViewerId={setMediaViewerId}
        />

        {/* Wave 20.38a — Fullscreen replay dialog. Same map + controls
            + data readout, but the map fills the viewport so the
            terrain + wind particles are actually legible. */}
        <Dialog
          open={replayFullscreen}
          fullScreen
          onClose={() => setReplayFullscreen(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#0e0e12',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ flex: 1, position: 'relative' }}>
            {activity?.gpsTrack && activity.gpsTrack.length >= 2 && (
              <RouteMap
                points={routePoints}
                highlights={routeHighlights}
                windReplay={activity.endedAt ? {
                  startedAt: activity.startedAt,
                  endedAt: activity.endedAt,
                } : undefined}
                terrain3D={features.terrain3D}
                terrainExaggeration={terrainExag}
                onWeatherChange={setCurrentWind}
                hideWeatherChip
                playbackMarker={playbackMarker}
                height="100%"
              />
            )}
            {/* Wave 20.38c — Unified fullscreen HUD. All metrics
                (time, distance, speed/pace, alt, power, HR, temp,
                humidity, wind) in one strip at the top so they
                don't stack on each other. Terrain slider docked
                just under it on the right. */}
            {currentSample && (
              <ReplayHudStrip
                anchor="top"
                large
                replayPos={replayPos}
                isRunning={activity?.type === 'outdoor_running'}
                sample={currentSample}
                sensor={currentSensor}
                wind={currentWind}
                units={units}
              />
            )}
            {features.terrain3D && (
              <Box
                sx={{
                  position: 'absolute',
                  // Wave 20.49cf — On mobile-web the top HUD wraps
                  // to two rows and this chip stacked over it. Dock
                  // it at the bottom of the map area instead so it
                  // sits above the replay controls, spanning full
                  // width. Desktop keeps the compact top-right pill.
                  top: { xs: 'auto', sm: 76 },
                  bottom: { xs: 12, sm: 'auto' },
                  right: 12,
                  left: { xs: 12, sm: 'auto' },
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(15,15,20,0.85)',
                  border: `1px solid ${colors.border}`,
                  width: { xs: 'auto', sm: 220 },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    sx={{
                      color: colors.brand,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      letterSpacing: '0.15em',
                      minWidth: 56,
                    }}
                  >
                    TERRAIN ×{terrainExag.toFixed(0)}
                  </Typography>
                  <Slider
                    size="small"
                    min={1}
                    max={10}
                    step={1}
                    value={terrainExag}
                    onChange={(_, v) =>
                      setTerrainExag(Array.isArray(v) ? v[0] : v)
                    }
                    sx={{ color: colors.brand, flex: 1 }}
                  />
                </Stack>
              </Box>
            )}
          </Box>
          {replayDurationSec > 0 && (
            <Box sx={{ p: 1.5 }}>
              <SessionReplayControls
                durationSec={replayDurationSec}
                positionSec={replayPos}
                playing={replayPlaying}
                speed={replaySpeed}
                autoSpeed={replayAutoSpeed}
                onPlayPause={() => setReplayPlaying((p) => !p)}
                onSeek={(sec) => setReplayPos(sec)}
                onSpeedChange={setReplaySpeed}
                onToggleFullscreen={() => setReplayFullscreen(false)}
                fullscreen
              />
              <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShareReplayOpen(true)}
                  sx={{ color: colors.brand, borderColor: colors.brand, fontWeight: 700 }}
                >
                  Drop this replay
                </Button>
              </Stack>
            </Box>
          )}
        </Dialog>

        <PeaksPanel
          activityId={id!}
          activityType={activity.type}
          onRequestPeakDrop={
            features.drops
              ? (p) =>
                  setPendingDrop({
                    kind: 'peak',
                    peakKind: p.kind,
                    value: p.value,
                    unit: p.unit,
                    contextLabel: p.contextLabel,
                    durationSec: p.durationSec ?? null,
                    distanceM: p.distanceM ?? null,
                  })
              : undefined
          }
          onSelectPeak={setSelectedPeak}
          selectedPeakKey={
            selectedPeak
              ? `${selectedPeak.kind}:${selectedPeak.startSec}:${selectedPeak.endSec}`
              : null
          }
        />

        {elevationProfile && elevationProfile.length > 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Elevation profile</Typography>
            <ElevationSvg
              points={elevationProfile}
              selectedPeak={selectedPeak ? {
                startSec: selectedPeak.startSec,
                endSec: selectedPeak.endSec,
                valueLabel: selectedPeak.valueLabel,
                durationLabel: selectedPeak.durationLabel,
                distanceLabel: selectedPeak.distanceLabel,
              } : null}
            />
          </Box>
        )}

      </Stack>

      <ShareCardDialog
        open={shareCardOpen}
        onClose={() => setShareCardOpen(false)}
        activity={activity}
        intervals={intervals}
      />
      {/* Wave 20.27 — Unified drop dialog. Opens for activity /
          peak / stat drops; onSubmit dispatches to the right API. */}
      <DropLinkDialog
        open={!!pendingDrop}
        onClose={() => setPendingDrop(null)}
        onSubmit={submitDrop}
        headline={
          pendingDrop?.kind === 'peak'
            ? pendingDrop.contextLabel
            : `${activityLabel(activity.type)} · ${new Date(activity.startedAt).toLocaleDateString()}`
        }
        disableFollowers={activity.visibility === 'public'}
      />

      <ShareReplayDialog
        open={shareReplayOpen}
        onClose={() => setShareReplayOpen(false)}
        sessionId={activity.id}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </PageLayout>
  );
}

function StatTile({
  label,
  value,
  icon,
  onDrop,
  dropping,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  onDrop?: () => void;
  dropping?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        position: 'relative',
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.25rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        {value}
      </Typography>
      {/* Wave 20.27 — Per-stat drop link. Small icon in the tile
          corner; mints a peak_drop for THIS metric so the rider can
          challenge someone to just distance / elevation / speed etc. */}
      {onDrop && value !== '—' && (
        <IconButton
          size="small"
          onClick={onDrop}
          disabled={dropping}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            color: colors.textMuted,
            '&:hover': { color: colors.brand },
          }}
          title="Drop this metric as a link"
        >
          <IosShareIcon fontSize="inherit" sx={{ fontSize: '0.9rem' }} />
        </IconButton>
      )}
    </Box>
  );
}

function ElevationSvg({
  points,
  selectedPeak,
}: {
  points: Array<[number, number]>;
  selectedPeak?: {
    startSec: number;
    endSec: number;
    valueLabel: string;
    durationLabel: string;
    distanceLabel?: string | null;
  } | null;
}) {
  const units = useUnitSystem();
  const W = 800;
  const H = 140;
  const PAD = 20;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xRange = Math.max(1, xMax - xMin);
  const yRange = Math.max(1, yMax - yMin);
  const toXY = ([x, y]: [number, number]) => ({
    x: PAD + ((x - xMin) / xRange) * (W - 2 * PAD),
    y: H - PAD - ((y - yMin) / yRange) * (H - 2 * PAD),
  });
  const path = points
    .map((pt, i) => {
      const { x, y } = toXY(pt);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const area = `${path} L ${W - PAD} ${H - PAD} L ${PAD} ${H - PAD} Z`;

  // Wave 20.32 — sub-path for the selected peak. Overlays on top of
  // the base line in brand color. A brand-tinted band spans the same
  // x-range to make it findable at a glance.
  const selSlice = selectedPeak
    ? points.filter(([t]) => t >= selectedPeak.startSec && t <= selectedPeak.endSec)
    : null;
  const selPath = selSlice && selSlice.length >= 2
    ? selSlice
        .map((pt, i) => {
          const { x, y } = toXY(pt);
          return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ')
    : null;
  const selBandX0 = selectedPeak
    ? PAD + ((selectedPeak.startSec - xMin) / xRange) * (W - 2 * PAD)
    : null;
  const selBandX1 = selectedPeak
    ? PAD + ((selectedPeak.endSec - xMin) / xRange) * (W - 2 * PAD)
    : null;
  const selMid = selSlice && selSlice.length > 0
    ? toXY(selSlice[Math.floor(selSlice.length / 2)])
    : null;

  return (
    <Box sx={{
      position: 'relative',
      p: 1.5,
      borderRadius: 2,
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`,
    }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="140" preserveAspectRatio="none">
        <path d={area} fill={`${colors.brand}22`} />
        <path d={path} fill="none" stroke={colors.brand} strokeWidth={2} opacity={selPath ? 0.45 : 1} />
        {selBandX0 != null && selBandX1 != null && selBandX1 > selBandX0 && (
          <rect
            x={selBandX0}
            y={PAD - 4}
            width={Math.max(2, selBandX1 - selBandX0)}
            height={H - PAD - (PAD - 4)}
            fill={`${colors.brand}22`}
            stroke="none"
          />
        )}
        {selPath && (
          <path d={selPath} fill="none" stroke={'#ffffff'} strokeWidth={3} />
        )}
        {selMid && (
          <circle cx={selMid.x} cy={selMid.y} r={4} fill={colors.brand} stroke={'#ffffff'} strokeWidth={1.5} />
        )}
      </svg>
      <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: colors.textMuted, textAlign: 'right' }}>
        {fmtElevationUnits(yMin, units)} → {fmtElevationUnits(yMax, units)}
      </Typography>
      {selectedPeak && selMid && (
        <Box
          sx={{
            position: 'absolute',
            // Approximate mapping from SVG viewBox x/y to the box's
            // CSS pixels. The svg is width:100% height:140 with a
            // ${W} x ${H} viewBox, preserveAspectRatio="none".
            left: `calc(${(selMid.x / W) * 100}% + 4px)`,
            top: `calc(${(selMid.y / H) * 140}px + 8px)`,
            transform: 'translate(-50%, -100%)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'rgba(15,15,20,0.9)',
            border: `1px solid ${colors.border}`,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography sx={{ color: colors.brand, fontWeight: 800, fontSize: '0.8rem', lineHeight: 1.1 }}>
            {selectedPeak.valueLabel}
          </Typography>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
            {[selectedPeak.durationLabel, selectedPeak.distanceLabel].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Wave 20.12 — inline visibility chip. Tap to cycle:
// public → followers → private → public.
function VisibilityChip({
  value,
  onChange,
}: {
  value: Visibility;
  onChange: (next: Visibility) => void;
}) {
  const order: Visibility[] = ['public', 'followers', 'private'];
  const next = order[(order.indexOf(value) + 1) % order.length];
  const meta = {
    public:    { label: 'Anyone',   Icon: PublicIcon, color: colors.brand },
    followers: { label: 'Friends',  Icon: GroupIcon,  color: colors.brand },
    private:   { label: 'Just me',  Icon: LockIcon,   color: colors.textMuted },
  }[value];
  const IconComp = meta.Icon;
  return (
    <Button
      onClick={() => onChange(next)}
      size="small"
      startIcon={<IconComp sx={{ fontSize: 16 }} />}
      sx={{
        color: meta.color,
        borderColor: colors.border,
        border: `1px solid ${colors.border}`,
        borderRadius: 999,
        px: 1.5,
        py: 0.25,
        fontWeight: 800,
        fontSize: '0.72rem',
        letterSpacing: '0.08em',
        textTransform: 'none',
        backgroundColor: colors.surface,
        '&:hover': { borderColor: meta.color, backgroundColor: colors.surface },
      }}
    >
      {meta.label}
    </Button>
  );
}

// Wave 20.49ca — Per-session media (parity with mobile
// SessionDetailScreen). Upload photo → thumbnail grid →
// fullscreen viewer. Uses fetch() + blob URLs because
// /media/:id requires a Bearer header that <img src> can't
// send.
interface MediaItem {
  id: string;
  mime: string;
  bytesLen: number;
  lat: number | null;
  lng: number | null;
  takenAt: string | null;
  durationMs: number | null;
  createdAt: string;
}

function AuthImage({
  mediaId,
  style,
}: {
  mediaId: string;
  style?: React.CSSProperties;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let created: string | null = null;
    (async () => {
      try {
        const res = await fetch(`${API}/media/${mediaId}`, { headers: tokenHeader() });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!alive) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      } catch { /* silent */ }
    })();
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [mediaId]);
  if (!url) {
    return (
      <Box
        sx={{
          ...style,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      />
    );
  }
  return <img src={url} alt="" style={style} />;
}

function MediaSection({
  sessionId,
  activityGpsFirst,
  items,
  onReload,
  viewerId,
  setViewerId,
}: {
  sessionId: string;
  activityGpsFirst: { lat: number; lng: number } | null;
  items: MediaItem[];
  onReload: () => void;
  viewerId: string | null;
  setViewerId: (id: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onPick = () => fileRef.current?.click();
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      if (activityGpsFirst) {
        body.append('lat', String(activityGpsFirst.lat));
        body.append('lng', String(activityGpsFirst.lng));
      }
      const res = await fetch(`${API}/sessions/${sessionId}/media`, {
        method: 'POST',
        headers: tokenHeader(),
        body,
      });
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = Array.isArray(j.message) ? j.message.join(', ') : String(j.message);
        } catch { /* ignore */ }
        setError(msg);
      } else {
        onReload();
      }
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Media {items.length > 0 ? `· ${items.length}` : ''}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onPick}
          disabled={uploading}
          sx={{
            borderColor: colors.brand,
            color: colors.brand,
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { borderColor: colors.brand, backgroundColor: `${colors.brand}18` },
          }}
        >
          {uploading ? 'Uploading…' : '+ Add photo'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/mp4,video/quicktime"
          onChange={onFile}
          style={{ display: 'none' }}
        />
      </Stack>
      {error && (
        <Typography sx={{ color: colors.danger, fontSize: '0.85rem', mb: 1 }}>
          {error}
        </Typography>
      )}
      {items.length === 0 ? (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
          No photos yet. Add one to pin the moment on the map.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {items.map((m) => (
            <Box
              key={m.id}
              onClick={() => setViewerId(m.id)}
              sx={{
                width: 112,
                height: 112,
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${colors.border}`,
                cursor: 'pointer',
                backgroundColor: colors.surface,
                '&:hover': { borderColor: colors.brand },
              }}
            >
              <AuthImage
                mediaId={m.id}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          ))}
        </Box>
      )}
      {viewerId && (
        <Box
          role="dialog"
          onClick={() => setViewerId(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            backgroundColor: 'rgba(0,0,0,0.94)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <AuthImage
            mediaId={viewerId}
            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain' }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 24,
              right: 24,
              color: '#fff',
              fontSize: 24,
              fontWeight: 800,
              userSelect: 'none',
            }}
          >
            ×
          </Box>
        </Box>
      )}
    </Box>
  );
}
